import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isAnyOf } from '@reduxjs/toolkit'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterBreadcrumbsMedChronAsset from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsMedChronAsset'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import * as Popover from '@radix-ui/react-popover'
import { Upload, InfoCircle, ShieldCheck } from '@/assets/icons'
import { kPaxtonAiName } from '@/constants/constants-strings'
import { matterPaths } from '@/routing/paths'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { selectAllFiles, selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import MedChronFileCard from '@/medical-chronology/asset/components/add-files/MedChronFileCard'
import { getMedChronUserFileCardState } from '@/medical-chronology/asset/utils/get-med-chron-file-card-state'
import { useMedChronAssetFilesUpload } from '../../hooks/useMedChronAssetFilesUpload'
import { useMedChronEditingLease } from '../../hooks/useMedChronEditingLease'
import { postPreviewAddFiles } from '../../fetch/post-preview-add-files'
import { MedChronAssetActions } from '../../store/slice'
import { addMedChronAssetListener } from '../../store/listeners'
import { UserFilesPollingActions } from '@/user_files/polling/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import {
  selectInitializeError,
  selectInitializePending,
  selectIsUpdateActive,
  selectMedChronAssetForMatter,
  selectMutationError,
  selectMutationPending,
} from '../../store/selectors'
import { FileAddDisposition, FileAddPreviewItem } from '../../store/schemas'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedChronEvent } from '@/analytics/utils/medchron-analytics'
import { AddFilesMatterFilesDialog } from './AddFilesMatterFilesDialog'
import { ADD_FILES_MODAL_TAB, type AddFilesModalInitialTab } from '@/chat-common/components/source-dialogs/AddFilesModal'
import { MatterAssetFileSourceMenu } from '@/matter-asset-common/files/components/MatterAssetFileSourceMenu'
import { MatterRecordStagedFileCard } from '@/matter-asset-common/files/components/MatterRecordStagedFileCard'
import { MedChronFileStatus } from '@/medical-chronology/asset/components/add-files/med-chron-file-status'
import { dispositionDisplay } from './disposition-display'
import { IncludedInChronologyDrawer } from './IncludedInChronologyDrawer'
import { refreshMatterAfterWriteAccessError } from '../../utils/refresh-matter-after-write-access-error'
import { EditingLeaseState } from '@/editing-leases/schemas'

const kPreviewDebounceMs = 400

/** The staged row shows a spinner and this line while the upload pipeline is still running. */
function inProgressLabel(status?: MedChronFileStatus): string | null {
  if (status === MedChronFileStatus.UPLOADING) return 'Uploading...'
  if (status === MedChronFileStatus.PROCESSING) return 'Processing...'
  return null
}

/**
 * Add Files: stage uploads and matter files, preview their dispositions against the
 * living chronology (net-new extraction only), then generate or update it.
 */
export default function AddFilesPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const matterId = activeMatterId ?? undefined
  const asset = useAppSelector((state: RootState) => selectMedChronAssetForMatter(state, { matterId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const staging = useAppSelector((state: RootState) => state.medChronAssetState.addFilesStaging)
  const uploadFilesByClientTmpId = useAppSelector(selectAllFiles)
  const uploadFilesByServerFileId = useAppSelector(selectUploadFilesByServerFileId)

  const initializePending = useAppSelector((state: RootState) => selectInitializePending(state, { matterId }))
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const mutationPending = useAppSelector((state: RootState) => selectMutationPending(state, { assetId: asset?.id }))
  const mutationError = useAppSelector((state: RootState) => selectMutationError(state, { assetId: asset?.id }))

  const assetId = asset?.id
  useEffect(() => {
    if (assetId) dispatch(MedChronAssetActions.setMutationError({ assetId, error: null }))
  }, [assetId, dispatch])
  useEffect(() => {
    if (matterId) dispatch(MedChronAssetActions.setInitializeError({ matterId, error: null }))
  }, [matterId, dispatch])

  const submitPending = initializePending || mutationPending

  const [filePickerTab, setFilePickerTab] = useState<AddFilesModalInitialTab | null>(null)
  const [previewItemsByFileId, setPreviewItemsByFileId] = useState<Record<string, FileAddPreviewItem>>({})
  const [isPreviewPending, setIsPreviewPending] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const stagedUserFileIds = staging.stagedUserFileIds
  const { filesById: stagedUserFilesById, error: stagedUserFilesLoadError } = useUserFiles(stagedUserFileIds)

  const upload = useMedChronAssetFilesUpload({ matterId: activeMatterId })
  const { canWrite, recordActivity, enabled: leaseEnabled, lease } = useMedChronEditingLease()
  const editingMatterRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (canWrite) editingMatterRef.current = matterId
    else if (matterId && editingMatterRef.current === matterId && leaseEnabled && lease?.state === EditingLeaseState.AVAILABLE) {
      dispatch(MedChronAssetActions.setAddFilesLeaseExpiredMatterId(matterId))
      navigate(matterPaths.chronology.index(matterId), { replace: true })
    }
  }, [canWrite, matterId, leaseEnabled, lease?.state, dispatch, navigate])

  // Upload progress and processing polls for this page's staged files count as activity.
  useEffect(() => {
    if (!canWrite) return
    return dispatch(
      addMedChronAssetListener({
        matcher: isAnyOf(UserFilesPollingActions.pollBatchReceived, UppyFileUploadActions.uploadProgress),
        effect: (action, listenerApi) => {
          const state = listenerApi.getState()
          const staging = state.medChronAssetState.addFilesStaging
          if (staging.matterId !== matterId) return
          if (UppyFileUploadActions.uploadProgress.match(action)) {
            const { clientTmpId, progress } = action.payload
            const upload = state.uppyFileUploadState.files[clientTmpId]?.client
            if (upload?.uploadStatus !== 'uploading' || !upload.serverFileId || !staging.stagedUserFileIds.includes(upload.serverFileId)) return
            const previousProgress = listenerApi.getOriginalState().uppyFileUploadState.files[clientTmpId]?.client.uploadProgress ?? 0
            if (progress > previousProgress) recordActivity()
            return
          }
          if (!UserFilesPollingActions.pollBatchReceived.match(action)) return
          const stillProcessing = staging.stagedUserFileIds.some((fileId) => {
            const status = action.payload.files[fileId]?.record.processing_status
            return status === UserFileProcessingStatus.Pending || status === UserFileProcessingStatus.Processing
          })
          if (stillProcessing) recordActivity()
        },
      })
    )
  }, [canWrite, dispatch, matterId, recordActivity])

  // Reset staging when entering for a different matter
  useEffect(() => {
    if (activeMatterId && staging.matterId !== activeMatterId) {
      dispatch(MedChronAssetActions.resetAddFilesStaging({ matterId: activeMatterId }))
    }
  }, [dispatch, activeMatterId, staging.matterId])

  // Fingerprint identity changes when the staged set OR any staged file's processing status
  // changes, so the preview re-runs when an upload finishes processing (not just when the id
  // list changes) - otherwise a NOT_READY disposition goes stale and keeps blocking the CTA
  // after the file completes. Staged ids are derived from the fingerprint so the effect can
  // depend on a single stable value (user_file ids are UUIDs and carry no ':').
  const stagedReadinessFingerprint = stagedUserFileIds
    .map((fileId) => {
      const uploadState = uploadFilesByServerFileId[fileId]
      const file = uploadState?.backend?.record ?? stagedUserFilesById[fileId]
      return `${fileId}:${file?.processing_status ?? 'unknown'}`
    })
    .join(',')
  const stagedPreviewIds = useMemo(
    () => (stagedReadinessFingerprint ? stagedReadinessFingerprint.split(',').map((entry) => entry.split(':')[0]) : []),
    [stagedReadinessFingerprint]
  )

  // Debounced disposition preview against the existing chronology
  useEffect(() => {
    if (!asset || stagedPreviewIds.length === 0) {
      setPreviewItemsByFileId({})
      setIsPreviewPending(false)
      setPreviewError(null)
      return
    }
    let cancelled = false
    const assetId = asset.id
    setIsPreviewPending(true)
    setPreviewError(null)
    const timeout = setTimeout(() => {
      postPreviewAddFiles(assetId, { user_file_ids: stagedPreviewIds })
        .then((response) => {
          if (cancelled) return
          const byId: Record<string, FileAddPreviewItem> = {}
          for (const item of response.items) byId[item.user_file_id] = item
          setPreviewItemsByFileId(byId)
          setPreviewError(null)
        })
        .catch((error) => {
          if (cancelled) return
          console.error('Error previewing add files:', error)
          setPreviewItemsByFileId({})
          refreshMatterAfterWriteAccessError(error, asset.matter_id, dispatch)
          setPreviewError("Couldn't verify this file. Try attaching again.")
        })
        .finally(() => {
          if (!cancelled) setIsPreviewPending(false)
        })
    }, kPreviewDebounceMs)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [asset, stagedPreviewIds, dispatch])

  // First-generation errors return to the chronology error banner; lease denials preserve staging.
  const wasPendingRef = useRef(false)
  const submittedRef = useRef<'initialize' | 'update' | null>(null)
  useEffect(() => {
    if (submittedRef.current && wasPendingRef.current && !submitPending && matterId) {
      const wasInitialization = submittedRef.current === 'initialize'
      const error = wasInitialization ? initializeError : mutationError
      submittedRef.current = null
      if (!error || (wasInitialization && typeof error === 'string')) {
        navigate(matterPaths.chronology.index(matterId))
      }
    }
    wasPendingRef.current = submitPending
  }, [submitPending, initializeError, mutationError, matterId, navigate])

  const stagedCards = stagedUserFileIds.map((fileId) => {
    const uploadState = uploadFilesByServerFileId[fileId]
    const file = uploadState?.backend?.record ?? stagedUserFilesById[fileId]
    const cardState = getMedChronUserFileCardState({ fileId, file, uploadState, loadError: stagedUserFilesLoadError && !file })
    // With an existing asset the disposition comes from the server preview (diff vs the chronology).
    // On a first-time generation there's no asset to diff against, so preview-add never runs — every
    // ready file is implicitly NEW. Surface that locally so the "Ready to process" line still shows.
    const preview: FileAddPreviewItem | undefined = asset
      ? previewItemsByFileId[fileId]
      : cardState.ready
      ? { user_file_id: fileId, display_name: cardState.fileName, disposition: FileAddDisposition.NEW, possible_duplicate_of: null }
      : undefined
    return { fileId, preview, ...cardState }
  })
  const pendingCards = staging.pendingUploadClientIds.map((clientTmpId) => {
    const uploadState = uploadFilesByClientTmpId[clientTmpId]
    return { clientTmpId, ...getMedChronUserFileCardState({ uploadState }) }
  })

  const hasBlockingDisposition = stagedCards.some((card) => card.preview && dispositionDisplay(card.preview)?.blocking)
  const hasUnreadyStaged = stagedCards.some((card) => !card.ready && !card.preview)
  const hasPendingUploads = pendingCards.length > 0
  const isAssetLoading = asset === undefined
  const hasPreviewPending = Boolean(asset && stagedCards.some((card) => card.ready && !card.preview))
  const totalStaged = stagedUserFileIds.length + pendingCards.length
  const readyStagedCount = stagedCards.filter((card) => card.ready).length
  // Nothing to do if every staged file is already in the chronology — block the no-op submit.
  const allStagedAlreadyIncluded =
    stagedCards.length > 0 && !hasPendingUploads && stagedCards.every((card) => card.preview?.disposition === FileAddDisposition.ALREADY_INCLUDED)

  const submitDisabled =
    stagedUserFileIds.length === 0 ||
    isAssetLoading ||
    submitPending ||
    isUpdateActive ||
    isPreviewPending ||
    hasPreviewPending ||
    Boolean(previewError) ||
    hasBlockingDisposition ||
    hasUnreadyStaged ||
    hasPendingUploads ||
    allStagedAlreadyIncluded

  const handleSubmit = useCallback(() => {
    if (submitDisabled || !activeMatterId) return
    submittedRef.current = asset ? 'update' : 'initialize'
    if (asset) {
      trackMedChronEvent(AnalyticsEvent.MedChronAssetUpdateClicked, { medChronAssetId: asset.id, medChronSelectedFilesCount: stagedUserFileIds.length })
      dispatch(MedChronAssetActions.applyUpdate({ assetId: asset.id, addUserFileIds: stagedUserFileIds }))
    } else {
      trackMedChronEvent(AnalyticsEvent.MedChronAssetGenerateClicked, { matterId: activeMatterId, medChronSelectedFilesCount: stagedUserFileIds.length })
      dispatch(MedChronAssetActions.initializeChronology({ matterId: activeMatterId, userFileIds: stagedUserFileIds }))
    }
  }, [submitDisabled, activeMatterId, asset, dispatch, stagedUserFileIds])

  const handleClearAll = useCallback(() => {
    staging.pendingUploadClientIds.forEach((clientTmpId) => upload.cancelUpload(clientTmpId))
    if (activeMatterId) dispatch(MedChronAssetActions.resetAddFilesStaging({ matterId: activeMatterId }))
  }, [staging.pendingUploadClientIds, upload, activeMatterId, dispatch])

  const dropzoneRootProps = upload.getRootProps()

  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <MatterBreadcrumbsMedChronAsset showAddFilesCrumb />

      <div
        className="flex-1 min-h-0 overflow-hidden flex flex-col items-center p-4 lg:p-6"
        {...dropzoneRootProps}
        // Disabled react-dropzone roots omit handlers, leaving browser file navigation enabled.
        onDragOver={upload.disabled ? (event) => event.preventDefault() : dropzoneRootProps.onDragOver}
        onDrop={upload.disabled ? (event) => event.preventDefault() : dropzoneRootProps.onDrop}
      >
        <input {...upload.getInputProps()} />
        <input ref={upload.fileInputRef} type="file" accept="application/pdf,.pdf" multiple hidden onChange={upload.onFilesPicked} />

        <div className="w-full max-w-[946px] flex-1 min-h-0 flex flex-col bg-brand-neutral-50 border border-brand-neutral-200 rounded-md overflow-hidden md:rounded-lg">
          {/* Header */}
          <div className="flex flex-col gap-3 px-6 pt-6 pb-4 md:flex-row md:items-start md:justify-between md:gap-6">
            <div className="flex flex-col gap-1">
              <h1 className="font-rethink-sans text-lg font-semibold leading-7 text-brand-neutral-950">Add Files</h1>
              <p className="hidden text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950 md:block">
                Upload or select an entire folder of medical records, no splitting or batching required.
              </p>
            </div>
            <div className="flex items-center gap-3.5 shrink-0">
              <Button className="md:hidden" size="sm" variant="outlined" color="primary" onClick={upload.open} disabled={upload.disabled}>
                Upload
              </Button>
              <Button className="max-md:hidden md:inline-flex" variant="outlined" color="primary" onClick={upload.open} disabled={upload.disabled}>
                Upload
              </Button>
              <Button className="md:hidden" size="sm" variant="filled" color="primary" onClick={() => setFilePickerTab(ADD_FILES_MODAL_TAB.MATTER_FILES)}>
                Matter Files
              </Button>
              <Button
                className="max-md:hidden md:inline-flex"
                variant="filled"
                color="primary"
                onClick={() => setFilePickerTab(ADD_FILES_MODAL_TAB.MATTER_FILES)}
              >
                Matter Files
              </Button>
            </div>
          </div>

          {/* Body: included drawer, then dropzone when empty or the staged list */}
          <div className={`flex-1 min-h-0 overflow-y-auto px-6 pb-0 flex flex-col gap-2 md:pb-6 ${totalStaged > 0 ? 'md:overflow-hidden' : ''}`}>
            {asset && asset.total_files > 0 && (
              <div className="shrink-0">
                <IncludedInChronologyDrawer assetId={asset.id} totalFiles={asset.total_files} />
              </div>
            )}
            {totalStaged === 0 ? (
              <div
                className={`w-full flex-1 min-h-[160px] rounded-lg flex flex-col items-center justify-center gap-3.5 px-4 pt-4 pb-9 transition-colors md:min-h-[320px] ${
                  upload.isDragActive ? 'bg-brand-50 outline-2 outline-dashed outline-brand-500' : ''
                }`}
              >
                <Upload className="w-6 h-6 text-brand-neutral-600" />
                <div className="flex flex-col items-center gap-2 text-center">
                  <p className="font-rethink-sans text-[15px] font-semibold leading-[25px] text-brand-neutral-600">Drag &amp; Drop to Upload or</p>
                  <MatterAssetFileSourceMenu matterId={activeMatterId} onUpload={upload.open} onOpenFilePicker={setFilePickerTab} />
                </div>
              </div>
            ) : (
              <div className="shrink-0 flex flex-col gap-3 pt-2 md:flex-1 md:min-h-0">
                <div className="shrink-0 flex flex-col gap-2 pb-4 pt-2">
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-rethink-sans text-base font-semibold leading-6 text-brand-neutral-950">Documents</p>
                    <p className="text-xs font-medium text-brand-neutral-600">
                      {readyStagedCount} of {totalStaged}
                    </p>
                  </div>
                  <div className="h-0.5 w-full bg-brand-neutral-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-500 transition-all"
                      style={{ width: `${totalStaged ? Math.round((readyStagedCount / totalStaged) * 100) : 0}%` }}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-3 [&>*]:shrink-0 md:flex-1 md:min-h-0 md:overflow-y-auto">
                  {stagedCards.map((card) => (
                    <MatterRecordStagedFileCard
                      key={card.fileId}
                      fileName={card.fileName}
                      inProgressLabel={inProgressLabel(card.status)}
                      disposition={dispositionDisplay(card.preview)}
                      duplicateOf={card.preview?.possible_duplicate_of?.display_name}
                      errorMessage={card.errorMessage || previewError || undefined}
                      onRemove={() => dispatch(MedChronAssetActions.unstageUserFileId({ fileId: card.fileId }))}
                    />
                  ))}
                  {pendingCards.map((card) => (
                    <MedChronFileCard
                      key={card.clientTmpId}
                      fileName={card.fileName}
                      status={card.status}
                      errorMessage={card.errorMessage || undefined}
                      onRemove={() => {
                        upload.cancelUpload(card.clientTmpId)
                        dispatch(MedChronAssetActions.removePendingUserFileUpload(card.clientTmpId))
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer CTA bar — wraps by available width so the primary CTA never clips off-screen */}
          <div className="border-t pl-4 pr-6 py-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-4">
            <div className="flex min-h-7 items-center gap-1 px-0.5 md:min-h-0 md:px-0">
              <Popover.Root>
                <Popover.Trigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-600 hover:text-brand-neutral-800 transition-colors"
                  >
                    <InfoCircle className="w-3.5 h-3.5 shrink-0" />
                    PDF files only
                  </button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    className="z-50 bg-white rounded-md shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)] p-5"
                    sideOffset={5}
                    align="start"
                  >
                    <p className="text-sm">
                      Currently supports PDF format.
                      <br />
                      Additional file types coming soon.
                      <br />
                      Max file size 1GB.
                    </p>
                    <Popover.Arrow className="fill-white" width={16} height={8} />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
              <span className="h-4 w-px bg-brand-neutral-300 shrink-0" aria-hidden="true" />
              <Popover.Root>
                <Popover.Trigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-600 hover:text-brand-neutral-800 transition-colors"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                    HIPAA
                  </button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    className="z-50 max-w-xs bg-white rounded-md shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)] p-5"
                    sideOffset={5}
                    align="start"
                  >
                    <p className="text-sm">HIPAA Certified. Your documents and data are secure, and never used to train {kPaxtonAiName}.</p>
                    <Popover.Arrow className="fill-white" width={16} height={8} />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </div>
            <div className="flex items-center gap-4 shrink-0 ml-auto md:gap-3">
              <Button className="whitespace-nowrap" variant="outlined" color="destructive" onClick={handleClearAll} disabled={totalStaged === 0}>
                Clear All
              </Button>
              <Button className="whitespace-nowrap" variant="filled" color="primary" onClick={handleSubmit} disabled={submitDisabled}>
                {submitPending ? (
                  <span className="inline-flex items-center gap-2">
                    <CircularProgressContinuousSized size={14} thickness={4} />
                    {asset ? 'Updating…' : 'Generating…'}
                  </span>
                ) : asset ? (
                  'Update Chronology'
                ) : (
                  'Generate Chronology'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <AddFilesMatterFilesDialog
        open={filePickerTab !== null}
        matterId={activeMatterId}
        initialSelectedIds={stagedUserFileIds}
        initialTab={filePickerTab ?? ADD_FILES_MODAL_TAB.MATTER_FILES}
        onClose={() => setFilePickerTab(null)}
        onApply={(userFileIds) => dispatch(MedChronAssetActions.setStagedUserFileIds({ fileIds: userFileIds }))}
      />

      <ConflictModal {...upload.conflictDialog} operation="upload" />
    </div>
  )
}
